import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatTabsModule } from "@angular/material/tabs";
import { RouterLink } from "@angular/router";
import { ButtonComponent, ButtonVariant, InputOutline } from "@sxm-content-creators/tools-ds";
import {
    AiConfig,
    BadgeConfigPayload,
    BadgeResponse,
    FreeformConfigPayload,
    ThemeConfigPayload,
    ThemeConfigResponse,
    ThemeFormValue
} from "../../models/theme.model";
import { buildAiConfig, createAiGroup } from "../theme-ai/ai-config.group";
import { ThemeAiEvaluationComponent } from "../theme-ai-evaluation/theme-ai-evaluation.component";
import { ThemeAiFiltersComponent } from "../theme-ai-filters/theme-ai-filters.component";
import { createDisplayGroup, ThemeDisplayComponent } from "../theme-display/theme-display.component";
import { createPwaGroup, ThemePwaComponent } from "../theme-pwa/theme-pwa.component";
import {
    createResponseRow,
    MAX_RESPONSES,
    ResponseRowGroup,
    rowNeedsContent,
    rowNeedsName,
    rowWouldBeActive,
    ThemeResponsesComponent
} from "../theme-responses/theme-responses.component";

export const THEME_NAME_MAX_LENGTH = 85;

export type ThemeFormMode = "create" | "edit";

@Component({
    selector: "app-theme-form",
    templateUrl: "./theme-form.component.html",
    styleUrl: "./theme-form.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        RouterLink,
        InputOutline,
        ButtonComponent,
        MatTabsModule,
        ThemeDisplayComponent,
        ThemePwaComponent,
        ThemeResponsesComponent,
        ThemeAiFiltersComponent,
        ThemeAiEvaluationComponent
    ]
})
export class ThemeFormComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);

    readonly mode = input<ThemeFormMode>("create");
    /** Existing theme name to pre-fill when editing. */
    readonly initialName = input<string | null>(null);
    /** Existing theme config (logo/wallpaper asset keys + preview URLs) to pre-fill when editing. */
    readonly initialThemeConfig = input<ThemeConfigResponse | null>(null);
    /** Existing response templates (badges) to pre-fill when editing. */
    readonly initialResponses = input<BadgeResponse[] | null>(null);
    /** Existing AI config (freeformConfig.aiConfig) to pre-fill the AI tabs when editing. */
    readonly initialAiConfig = input<AiConfig | null>(null);
    /** Disables the primary action while a create/update request is in flight. */
    readonly isSubmitting = input(false);
    /** Emits the theme form value (name + optional freeform config) when the primary is triggered. */
    readonly formSubmit = output<ThemeFormValue>();

    readonly form = this.formBuilder.nonNullable.group({
        name: this.formBuilder.nonNullable.control("", [
            Validators.required,
            Validators.maxLength(THEME_NAME_MAX_LENGTH)
        ]),
        display: createDisplayGroup(this.formBuilder),
        pwa: createPwaGroup(this.formBuilder),
        responses: this.formBuilder.array<ResponseRowGroup>([]),
        ai: createAiGroup(this.formBuilder)
    });

    private readonly nameValue = toSignal(this.form.controls.name.valueChanges, {
        initialValue: this.form.controls.name.value
    });

    // Ticks on every response-card edit. Read for invalidation only (the emitted value is unused),
    // since a FormArray's validity isn't reactive on its own — same trick as tenant-form's
    // `formEvents`.
    private readonly responseEvents = toSignal(this.form.controls.responses.valueChanges, {
        initialValue: null
    });

    /**
     * Response cards the backend would reject: content with no short name, or a short name with no
     * response text or image. Either fails the whole save, so the primary stays disabled while a
     * card is in one of those states — each already shows an inline error (see ThemeResponsesComponent).
     */
    private readonly invalidResponses = computed(() => {
        this.responseEvents(); // subscribe: recompute when any card changes
        return this.form.controls.responses.controls
            .map(row => row.getRawValue())
            .filter(row => rowNeedsName(row) || rowNeedsContent(row));
    });

    // Derive validity from the name signal (not the non-reactive `form.invalid`) so the
    // primary re-enables as the user types. Whitespace-only counts as empty.
    readonly isSubmitDisabled = computed(() => {
        const trimmedLength = this.nameValue().trim().length;
        return (
            trimmedLength === 0 ||
            trimmedLength > THEME_NAME_MAX_LENGTH ||
            this.invalidResponses().length > 0 ||
            this.isSubmitting()
        );
    });

    readonly title = computed(() => (this.mode() === "edit" ? "Edit Theme" : "Create New Theme"));
    readonly primaryLabel = computed(() => (this.mode() === "edit" ? "Save" : "Create Theme"));

    ngOnInit(): void {
        const name = this.initialName();
        if (name) {
            this.form.controls.name.setValue(name);
        }
        this.seedDisplay();
        this.seedPwa();
        this.seedResponses();
        this.seedAi();
    }

    // Replace the empty Display group with one pre-filled from the loaded theme config (edit mode).
    private seedDisplay(): void {
        const config = this.initialThemeConfig();
        if (config) {
            this.form.setControl("display", createDisplayGroup(this.formBuilder, config));
        }
    }

    // Replace the empty PWA group with one pre-filled from the loaded theme config (edit mode).
    private seedPwa(): void {
        const config = this.initialThemeConfig();
        if (config) {
            this.form.setControl("pwa", createPwaGroup(this.formBuilder, config));
        }
    }

    // Replace the empty AI group with one pre-filled from the loaded aiConfig (edit mode).
    private seedAi(): void {
        const aiConfig = this.initialAiConfig();
        if (aiConfig) {
            this.form.setControl("ai", createAiGroup(this.formBuilder, aiConfig));
        }
    }

    onSubmit(): void {
        if (this.isSubmitDisabled()) return;
        this.formSubmit.emit(this.buildValue());
    }

    // Always render a fixed set of cards (all optional). Existing badges (sorted by displayOrder)
    // fill the leading slots; the rest are empty.
    private seedResponses(): void {
        const badges = [...(this.initialResponses() ?? [])]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .slice(0, MAX_RESPONSES);
        for (let index = 0; index < MAX_RESPONSES; index++) {
            this.form.controls.responses.push(createResponseRow(this.formBuilder, badges[index]));
        }
    }

    private buildValue(): ThemeFormValue {
        return {
            name: this.form.controls.name.value.trim(),
            themeConfig: this.buildThemeConfig(),
            freeformConfig: this.buildFreeformConfig()
        };
    }

    // Assemble the freeform sections this form owns: whatever AI fields the user filled, plus
    // badges when at least one card is filled. Empty nested sections are left out; the container
    // itself is always returned, since the backend requires the key.
    private buildFreeformConfig(): FreeformConfigPayload {
        const freeform: FreeformConfigPayload = {};
        const aiConfig = buildAiConfig(this.form.controls.ai);
        if (aiConfig) freeform.aiConfig = aiConfig;
        const badgesConfig = this.buildBadges();
        if (badgesConfig.length) freeform.badgesConfig = badgesConfig;
        return freeform;
    }

    // Emit only the fields the Display + PWA tabs own, omitting empties. Asset-key controls carry
    // the uploaded key; the paired `*Url` controls are preview-only (never sent). The on-page "Logo"
    // field is backed by tenantLogo (see ThemeDisplayComponent). Colors/text are trimmed.
    // Returns `{}` when nothing across either tab has been set — the key is always sent.
    private buildThemeConfig(): ThemeConfigPayload {
        const { tenantLogoAssetKey, hostViewWallpaperAssetKey } = this.form.controls.display.getRawValue();
        const pwa = this.form.controls.pwa.getRawValue();
        const payload: ThemeConfigPayload = {};
        if (tenantLogoAssetKey) payload.tenantLogoAssetKey = tenantLogoAssetKey;
        if (hostViewWallpaperAssetKey) payload.hostViewWallpaperAssetKey = hostViewWallpaperAssetKey;
        if (pwa.pwaName.trim()) payload.pwaName = pwa.pwaName.trim();
        if (pwa.pwaNameShort.trim()) payload.pwaNameShort = pwa.pwaNameShort.trim();
        if (pwa.pwaDescription.trim()) payload.pwaDescription = pwa.pwaDescription.trim();
        if (pwa.pwaThemeColor.trim()) payload.pwaThemeColor = pwa.pwaThemeColor.trim();
        if (pwa.pwaBackgroundColor.trim()) payload.pwaBackgroundColor = pwa.pwaBackgroundColor.trim();
        if (pwa.pwaAppIcon192AssetKey) payload.pwaAppIcon192AssetKey = pwa.pwaAppIcon192AssetKey;
        if (pwa.pwaAppIcon512AssetKey) payload.pwaAppIcon512AssetKey = pwa.pwaAppIcon512AssetKey;
        return payload;
    }

    // Keep only filled cards; assign displayOrder from their current (drag) position (1-based).
    // `rowWouldBeActive` is the same rule that enables the card's "Response is active" toggle, so
    // a badge can't be sent active in a state the card wouldn't allow.
    private buildBadges(): BadgeConfigPayload[] {
        return this.form.controls.responses.controls
            .map(row => row.getRawValue())
            .filter(row => row.name.trim() || row.response.trim() || row.image)
            .map((row, index) => {
                const badge: BadgeConfigPayload = {
                    displayOrder: index + 1,
                    responseIsActive: rowWouldBeActive(row)
                };
                if (row.id) badge.id = row.id;
                if (row.name.trim()) badge.name = row.name.trim();
                if (row.response.trim()) badge.response = row.response.trim();
                if (row.image) badge.image = row.image;
                return badge;
            });
    }

    protected readonly maxLength = THEME_NAME_MAX_LENGTH;
    protected readonly ButtonVariant = ButtonVariant;
}
